<template>
  <div class="container">
    <div class="content-block-large swiper">
      <div class="swiper__wrapper" data-theme="summer-sky">
        <ClientOnly>
          <div class="swiper__container slider-thumbs">
            <SlidersSliderNavigation @previous="thumbSwiper.prev()" @next="thumbSwiper.next()" />
            <!-- Thumbs swiper -->
            <swiper-container
              ref="thumbRef"
              v-bind="swiperOptions"
              class="swiper__inner-wrapper"
              @swiperslidechange="handleSlideChange">
              <swiper-slide
                v-for="(person, index) in data"
                :key="
                  JSON.stringify([person.firstName?.value, person.lastName?.value].join('')) ??
                  index
                "
                class="swiper__slide"
                @click="handleClick(index)">
                <div class="slide__thumb">
                  <figure class="avatar">
                    <img
                      v-if="person.profilePicture && person.profilePicture.value?.url"
                      v-bind="useEpiAttrs(person.profilePicture)"
                      width="150"
                      :src="person.profilePicture.value.url + '?w=150'"
                      :alt="person.profilePicture.value?.alt ?? ''" />
                  </figure>
                  <div class="slide__person">
                    <h3 v-if="person.firstName" v-bind="useEpiAttrs(person.firstName)">
                      {{ person.firstName.value }}
                    </h3>
                    <em v-if="person.jobTitle" v-bind="useEpiAttrs(person.jobTitle)">{{
                      person.jobTitle.value
                    }}</em>
                  </div>
                  <ImagesSvg class="slide__arrow" icon="arrow-down" :width="56" />
                </div>
              </swiper-slide>
            </swiper-container>
          </div>
        </ClientOnly>
      </div>
      <ClientOnly>
        <div class="swiper__container swiper-container-autoheight">
          <!-- Main swiper -->
          <swiper-container
            ref="swiperRef"
            class="swiper-wrapper"
            :allow-touch-move="false"
            :loop="true"
            :auto-height="true">
            <swiper-slide
              v-for="person in data"
              :key="`${person.profilePicture}`"
              class="auto-height">
              <article class="slide-large">
                <div class="slide-summary" :class="{ open: slideDetailsOpen }">
                  <div class="slide-intro" :class="{ expanded: slideDetailsOpen }">
                    <figure class="avatar">
                      <img
                        v-if="person.profilePicture && person.profilePicture.value?.url"
                        v-bind="useEpiAttrs(person.profilePicture)"
                        width="150"
                        :src="person.profilePicture.value.url + '?w=150'"
                        :alt="person.profilePicture.value?.alt ?? ''" />
                    </figure>
                    <q class="quote"
                      ><h2 v-if="person.quote" v-bind="useEpiAttrs(person.quote)">
                        {{ person.quote.value }}
                      </h2>
                      <em v-if="person.firstName" v-bind="useEpiAttrs(person.firstName)"
                        >{{ person.firstName.value }},
                      </em>
                      <em v-if="person.jobTitle" v-bind="useEpiAttrs(person.jobTitle)">{{
                        person.jobTitle.value
                      }}</em></q
                    >
                    <p />
                    <div class="more-link__container">
                      <Transition name="more-link">
                        <LinksIconTextLink
                          v-show="!slideDetailsOpen"
                          icon="arrow-right"
                          :icon-size="16"
                          icon-color="oker"
                          text="lees verder"
                          text-color="navy-blue"
                          class="more-link"
                          @click="showDetails" />
                      </Transition>
                    </div>
                  </div>
                  <figure class="slide-img">
                    <img
                      v-if="person.image && person.image.value?.url"
                      width="680"
                      :src="person.image.value.url + '?w=680'"
                      :alt="person.image.value.alt ?? ''" />
                  </figure>
                </div>
                <Transition name="slide-details">
                  <div v-if="slideDetailsOpen && person.moreAboutMe">
                    <BlocksWerkenbijContentArea
                      class="slide-details"
                      :class="{ open: slideDetailsOpen }"
                      :content="person.moreAboutMe" />
                  </div>
                </Transition>
              </article>
            </swiper-slide>
          </swiper-container>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PersonBlockResponseModel } from '@api';

defineProps<{ data: PersonBlockResponseModel[] }>();

const swiperRef = ref(null);
const thumbRef = ref(null);
const slideDetailsOpen = ref(false);

const thumbSwiper = useSwiper(thumbRef);
const mainSwiper = useSwiper(swiperRef);

function handleSlideChange(e: CustomEvent): void {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  mainSwiper.instance.value?.slideToLoop(e.detail[0].realIndex);
  slideDetailsOpen.value = false;
}

function handleClick(index: number): void {
  thumbSwiper.instance.value?.slideToLoop(index);
  slideDetailsOpen.value = false;
}

function showDetails(): void {
  slideDetailsOpen.value = true;

  // Arbitrary milisecond delay for update to auto-height content
  setTimeout(() => {
    mainSwiper.instance.value?.updateAutoHeight();
  }, 1);
}

const swiperOptions = {
  loop: true,
  slidesPerView: 3,
  centeredSlides: true,
  breakpoints: {
    [BREAK_POINTS.SM]: {
      slidesPerView: 3,
    },
    [BREAK_POINTS.MD]: {
      slidesPerView: 5,
    },
  },
};
</script>

<style lang="scss" scoped>
.swiper {
  &__wrapper {
    position: relative;
    padding: px(24) px(30) px(16);
    border-radius: px(12);
    margin-bottom: px(8);
    color: var(--theme-color);
    background-color: var(--theme-bg-color);
  }

  &__slide {
    position: relative;
    width: 100%;
    height: 100%;
    flex-shrink: 0;
    transition-property: transform;
  }

  .slide {
    &__thumb {
      display: grid;
      grid-template-columns: auto;
      grid-template-rows: auto 1fr auto;
      place-items: start center;
      height: 100%;
      width: 100%;
      hyphens: auto;
      overflow: hidden;
      margin: 0 auto;

      .avatar {
        width: px(80);
        height: px(80);
        margin-bottom: px(16);
        border-radius: 50%;
        grid-row: 1 / 2;
        transition: border-color 0.2s;
        cursor: pointer;

        &:hover {
          border: px(4) solid var(--color-blue-alt);
        }
      }
    }

    &__person {
      grid-column: 1 / 2;
      grid-row: 2 / 3;
      align-self: flex-start;
      padding-inline: px(12);

      h3 {
        display: block;
        word-break: keep-all;
        margin-bottom: 0;
        font-size: px(12);
        line-height: 1.1em;
      }

      em {
        display: block;
        word-break: keep-all;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: px(12);
      }
    }

    &__arrow {
      margin-top: px(24);
      opacity: 0;
      width: px(32);
      height: px(32);
      grid-row: 3 / 4;
    }
  }

  &__container {
    position: relative;
    margin-inline: auto;
    overflow: hidden;
    list-style: none;
    padding: 0;
    z-index: 1;

    .swiper-slide-active {
      .slide {
        &__thumb {
          .avatar {
            border: px(4) solid var(--color-white);
          }
        }

        &__arrow {
          opacity: 1;
        }
      }
    }

    &.swiper-container-autoheight {
      height: auto;

      .swiper-wrapper {
        position: relative;
        display: flex;
        width: 100%;
        height: 100%;
        z-index: 1;
        align-items: flex-start;
        box-sizing: content-box;
        transition-property: transform, height;

        .auto-height {
          height: auto;
        }

        .slide-large {
          position: relative;
          margin-bottom: px(24);
        }

        .slide-summary {
          display: flex;
          flex-direction: column;
          background-color: var(--theme-bg-color);
          border-radius: px(12);
          overflow: hidden;
          padding: px(20);

          &.open {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }
        }

        .slide-intro {
          display: grid;
          order: 2;
          grid-template-columns: 1fr;
          gap: px(24);
          grid-template-areas:
            'slideQuote'
            'slideIntro'
            'slideMoreLink';
          padding-bottom: px(24);

          &.expanded {
            padding-bottom: 0;
            gap: 0;
          }

          .avatar {
            display: none;
            grid-area: slideAvatar;
            margin-top: px(120);
            width: px(150);
            height: px(150);
          }
        }

        .more-link {
          opacity: 1;

          &__container {
            justify-self: flex-end;
            grid-area: slideMoreLink;
            min-height: px(32);
          }

          &-leave-active {
            opacity: 0;
            transition: opacity 0.2s ease;
          }
        }

        .slide-img {
          position: relative;
          align-self: flex-start;
          order: 1;
          width: 100%;
          margin: 0 auto;
          margin-bottom: px(24);

          img {
            width: 100%;
            max-width: 100%;
            height: auto;
            border-radius: px(12);
          }
        }

        .slide-details {
          opacity: 0;
          background-color: var(--theme-bg-color);
          border-bottom-left-radius: px(12);
          border-bottom-right-radius: px(12);
          padding: px(0) px(20) px(20);

          :deep(p + p) {
            margin-top: px(16);
          }

          &.open {
            opacity: 1;
          }

          &-enter-active,
          &-leave-active {
            transition: opacity 0.2s ease;
          }

          &-enter-from,
          &-enter-to {
            opacity: 1;
          }
        }
      }

      .quote {
        color: var(--color-navy-blue);

        h2 {
          margin-bottom: 0;

          &::before {
            content: '“';
          }

          &::after {
            content: '”';
          }
        }
      }
    }

    &.slider-thumbs {
      position: static;

      .swiper__slide {
        height: auto;
        width: px(80);
      }

      /* stylelint-disable-next-line no-descending-specificity -- exc */
      .avatar {
        position: relative;
        display: block;
      }

      .slide__person {
        max-width: px(92);
      }

      .slider-nav {
        position: absolute;
      }
    }
  }

  &__inner-wrapper {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    z-index: 1;
    transition-property: transform;
    box-sizing: content-box;
  }

  @include media-breakpoint-up(xs) {
    &__wrapper {
      padding: px(24) px(48) px(8);
    }

    &__container {
      &.slider-thumbs {
        .slide__person {
          max-width: px(100);
        }
      }
    }

    .slide {
      &__person {
        em {
          white-space: initial;
          overflow: initial;
          text-overflow: initial;
        }
      }
    }
  }

  @include media-breakpoint-up(md) {
    &__container {
      &.slider-thumbs {
        .slide__person {
          max-width: px(150);
        }
      }
    }
  }

  @include media-breakpoint-up(lg) {
    &__wrapper {
      padding: px(32) px(80) px(40);
    }

    .slide {
      &__person {
        padding-inline: initial;

        h3 {
          margin-bottom: initial;
          font-size: px(22);
          line-height: initial;
        }

        em {
          margin-bottom: 0;
          font-size: initial;
          line-height: 1.1em;
        }
      }

      &__thumb {
        .avatar {
          border: px(12) solid transparent;
          width: px(150);
          height: px(150);

          &:hover {
            border: px(12) solid var(--color-blue-alt);
          }
        }
      }
    }

    .swiper-slide-active {
      .slide {
        &__thumb {
          .avatar {
            border: px(12) solid var(--color-white);

            /* stylelint-disable-next-line max-nesting-depth -- exc */
            &:hover {
              border: px(12) solid var(--color-blue-alt);
            }
          }
        }
      }
    }

    &__container {
      &.swiper-container-autoheight {
        .swiper-wrapper {
          .slide-summary {
            height: auto;
            flex-direction: row;
            justify-content: space-between;
            padding: 0 0 0 px(40);
          }

          .slide-intro {
            order: 1;
            margin-right: px(20);
            width: calc(50% - px(90));
            grid-template-columns: px(150) 1fr;
            gap: px(24);
            grid-template-areas:
              'slideAvatar slideQuote'
              'slideIntro slideIntro'
              '. slideMoreLink';

            /* stylelint-disable-next-line max-nesting-depth -- exc */
            .avatar {
              display: block;
            }

            /* stylelint-disable-next-line max-nesting-depth -- exc */
            &.expanded {
              padding-bottom: px(24);
              gap: px(24);
            }
          }

          .slide-img {
            order: 2;
            margin-left: px(20);
            margin-bottom: 0;
            max-width: none;
            width: calc(50% + px(50));
            align-self: stretch;

            /* stylelint-disable-next-line max-nesting-depth -- exc */
            img {
              border-radius: 0;
              height: 100%;
              position: absolute;
              inset: 0;
              object-fit: cover;
            }
          }

          .slide-details {
            padding: 0 px(20) px(20);
            column-count: 2;
            column-gap: px(40);
          }
        }

        .quote {
          margin-top: px(120);

          h2 {
            margin-bottom: px(16);
          }
        }
      }
    }
  }

  @include media-breakpoint-up(xl) {
    .swiper__container {
      &.swiper-container-autoheight {
        .swiper-wrapper {
          .slide-summary {
            padding-left: px(150);
          }

          .slide-details {
            padding: px(40) px(150);
          }
        }
      }
    }
  }
}
</style>
